import type {
  ComponentTypeConfig,
  ComponentTypeName,
} from './componentFormTypes';
import { classIdFor, COMPONENT_PART_NAMES, COMPONENT_PLURALS } from './componentFormTypes';

/**
 * Phase 6e2a — §50.1 per-type spec table as literal config objects (the
 * legacy form.js constructor args, extracted verbatim from
 * components/<type>/view/form.js on the live branch).
 *
 * validatedFields = LIVE entries only (commented-out legacy entries omitted —
 * fan/coil/gas_heater/energy_wheel/phx ship empty lists, §50.1).
 * Per-type model layer = these urlRoot plurals, nothing more (parity 343 /
 * [NEG]#113 — no legacy model blobs).
 */

const make = (
  type: ComponentTypeName,
  cfg: Omit<ComponentTypeConfig, 'type' | 'plural' | 'partName' | 'classID'>
): ComponentTypeConfig => ({
  type,
  plural: COMPONENT_PLURALS[type],
  partName: COMPONENT_PART_NAMES[type],
  classID: classIdFor(type),
  ...cfg,
});

export const COMPONENT_FORM_CONFIGS: Record<ComponentTypeName, ComponentTypeConfig> = {
  fan: make('fan', {
    subtabs: [
      'section',
      'properties',
      'performance',
      'acoustics',
      'motor',
      'motor_base',
      'motor_controller',
      'fm_base',
      'isolation',
      'sheave_belts',
      'airflow_station',
      'remarks',
    ],
    withRating: true,
    validatedFields: [],
    catalogueFields: [
      {
        targetField: 'master_fan_id',
        paramsSourceField: 'fan_params',
        sourceURL: '/api/v1.0/cms/1/v1.0.0/ahu/fans/all/',
        selectedModelURL: '/api/v1.0/cms/1/v1.0.0/ahu/fans/',
        componentName: 'fan',
        componentsName: 'fans',
        paramsFields: [
          'id',
          'master_fan_id',
          'type_id',
          'arr_id',
          'mfg_id',
          'series_id',
          'amca_class_id',
          'qty_of_blades_id',
          'bearing_life_id',
          'housed_discharge_id',
          'shaft_mtl_id',
          'wheel_mtl_id',
          'rotation_id',
          'wheel_dia_nominal_id',
          'wheel_dia_nominal_mm_id',
          'dia_nominal_mm_id',
          'wheel_size_id',
          'wheel_dia_percent',
          'wheel_dia_percent_id',
          'wheel_width_percent_id',
          'wheel_width_full_size',
          'style_id',
          'fan_inlet_vane_id',
          'cube_housing_id',
        ],
        noFetchCount: true,
        counterElementWhenInRatingMode: true,
      },
      {
        targetField: 'master_motor_id',
        paramsSourceField: 'motor_params',
        sourceURL: '/api/v1.0/cms/1/v1.0.0/ahu/motors/all/',
        selectedModelURL: '/api/v1.0/cms/1/v1.0.0/ahu/motors/all/',
        componentName: 'motor',
        componentsName: 'motors',
        paramsFields: [
          'master_fan_id',
          'mfg_id',
          'type_id',
          'series_id',
          'efficiency_id',
          'enclosure_id',
          'housing_id',
          'speed_nominal_id',
          'motor_power',
          'shaft_grounding_id',
          'mounting_id',
          'motor_nema_frame_id',
          'ip_rating_id',
          'id',
        ],
        filtersCount: 3, // main / fan-link / voltage (§50.1)
        noFetchCount: true,
      },
      {
        targetField: 'master_spring_isolator_id',
        paramsSourceField: 'si_params',
        sourceURL: '/api/v1.0/cms/1/v1.0.0/ahu/fans/isolators/spring/all/',
        selectedModelURL: '/api/v1.0/cms/1/v1.0.0/ahu/fans/isolators/spring/all',
        componentName: 'fan_si',
        componentsName: 'fan_sis',
        paramsFields: ['mfg_id', 'series_id', 'isolator_type_id'],
        spnCustomSourceField: 'quick_name',
        noFetchCount: true,
      },
      // motor_base field COMMENTED OUT in legacy (§50.1) — not ported.
    ],
  }),

  coil: make('coil', {
    subtabs: ['section', 'properties', 'performance', 'remarks'],
    withRating: true,
    validatedFields: [],
    catalogueFields: [
      {
        targetField: 'master_coil_id',
        paramsSourceField: 'coil_params',
        sourceURL: '/api/v1.0/cms/1/v1.0.0/ahu/coils/all/',
        selectedModelURL: '/api/v1.0/cms/1/v1.0.0/ahu/coils/',
        componentName: 'coil',
        componentsName: 'coils',
        // 56 paramsFields — tube/fin/casing/conn/distributor/evaporator (§50.1).
        paramsFields: [
          'mfg_id', 'spn', 'series_id', 'type_id', 'fluid_id', 'rows_id',
          'fpi_id', 'fl', 'fh', 'duty_id', 'tube_mtl_id', 'tube_cleanable',
          'tube_dia_id', 'tube_wall_thick_id', 'tube_vert_spacing_id',
          'tube_hor_spacing_id', 'tube_interior_id', 'circuiting_id',
          'fin_mtl_id', 'fin_thick_id', 'fin_pattern_id', 'fin_finish_id',
          'inlet_conn_dia_id', 'inlet_header_dia', 'outlet_conn_dia_id',
          'outlet_header_dia_id', 'inlet_header_dia_id', 'conn_end_id',
          'conn_hand_id', 'conn_mtl_id', 'conn_pipe_end_id', 'casing_mtl_id',
          'casing_type_id', 'casing_flange_width_id',
          'casing_flange_header_side_id', 'casing_flange_return_side_id',
          'casing_flange_top_id', 'casing_flange_btm_id',
          'casing_stacking_flange', 'condensate_conn_size_id',
          'aux_side_conn', 'fastening_method_id', 'mounting_holes',
          'vent_drain_id', 'evaporator_mode_id', 'header_mtl_id',
          'distributor_1_dia', 'distributor_2_dia', 'distributor_3_dia',
          'distributor_4_dia', 'outlet_header_dia', 'moisture_eliminator',
          'nitrogen_charge', 'vacuum_breaker', 'thermostatic_vent',
          'conn_rotation_id',
        ],
        noFetchCount: true,
      },
    ],
  }),

  filter: make('filter', {
    subtabs: ['section', 'properties', 'performance', 'frame', 'pricing', 'remarks'],
    withRating: false, // setupRatingMode present without withRating gating (§50.1)
    validatedFields: [
      'master_filter_id',
      'master_frame_id',
      'distance_from_section_ups',
      'size_and_qty_id',
      'size_qty_id',
      'face_velocity_actual',
      'frame_loading_id',
      'has_frame',
      'height_from_floor',
      'arrangement_id',
      'airflow_actual',
      'mounting_angle',
    ],
    catalogueFields: [
      {
        targetField: 'master_filter_id',
        paramsSourceField: 'filter_params',
        sourceURL: '/api/v1.0/cms/1/v1.0.0/ahu/filters/all/',
        // The FILTER catalogue's first-detailed fetch runs on the SEARCHING
        // base, not on the detailed one. Legacy special-cases exactly this
        // field — `getFirstModelDetailed` branches on
        // `baseUrl.split('/')[7] == "filters"` and uses `baseUrl` instead of
        // `detailedBaseUrl` (cms.components.pages.js:250-275) — because the
        // two endpoints do NOT filter alike: `/filters/all/` runs
        // `CMSFilterSearch` (every catalogue param), while `/filters/` runs
        // `CMSAHUFilterFilter` (cms/filter/frame.py:265, `Meta.fields = []`),
        // which honours spn / mfg_id / series_id and silently IGNORES
        // type, merv, efficiency, depth, UL class, arrangement and bank
        // configuration. Pointed at `/filters/`, the "catalogue narrowed to
        // one row, take it" auto-pick fetched the first row of a nearly
        // unfiltered list and selected a filter nobody asked for, writing its
        // own merv/depth back over the user's (reported 2026-10-14: Depth 4"
        // + MERV 13, one real match, picked a 12" MERV 16 instead).
        //
        // The FRAME field below keeps the detailed base on purpose: its two
        // endpoints share one filterset (`CMSFrameFilter`,
        // filters_viewset.py:409/814), so there is nothing to fix there and
        // the payload autoSelectFrame reads stays as verified.
        selectedModelURL: '/api/v1.0/cms/1/v1.0.0/ahu/filters/all/',
        componentName: 'filter',
        componentsName: 'filters',
        paramsFields: [
          'mfg_id',
          'series_id',
          'type_id',
          'merv_id',
          'efficiency_id',
          'ul_class_id',
          'depth_nominal_id',
          'cms_id',
        ],
        filterFields: [
          'mfg_id',
          'series_id',
          'type_id',
          'merv_id',
          'efficiency_id',
          'ul_class_id',
          'depth_nominal_id',
          'size_and_qty_id',
          'size_qty_id',
          'arrangement_id',
        ],
        filtersCount: 2,
        skipFilteredCountOnInit: true,
        spnCustomSourceField: 'info',
      },
      {
        targetField: 'master_frame_id',
        paramsSourceField: 'frame_params',
        sourceURL: '/api/v1.0/cms/1/v1.0.0/ahu/filters/frames/all/',
        selectedModelURL: '/api/v1.0/cms/1/v1.0.0/ahu/filters/frames/',
        componentName: 'frame',
        componentsName: 'frames',
        paramsFields: ['mfg_id', 'frame_type_id', 'mtl_id', 'series_id', 'frame_loading_id'],
        filterFields: ['mfg_id', 'frame_type_id', 'mtl_id', 'series_id', 'frame_loading_id'],
        filtersCount: 2,
        skipFilteredCountOnInit: true,
        spnCustomSourceField: 'info',
      },
    ],
  }),

  eliminator: make('eliminator', {
    subtabs: ['section', 'properties', 'performance', 'frame', 'remarks'],
    withRating: false,
    validatedFields: [
      'master_eliminator_id',
      'master_frame_id',
      'distance_from_section_ups',
      'size_and_qty_id',
      'face_velocity_actual',
      'has_frame',
      'frame_loading_id',
      'height_from_floor',
      'airflow_actual',
    ],
    catalogueFields: [
      {
        targetField: 'master_eliminator_id',
        paramsSourceField: 'eliminator_params',
        sourceURL: '/api/v1.0/cms/1/v1.0.0/ahu/eliminators/all/',
        // The first-detailed fetch runs on the SEARCHING base, as the counts
        // do. `/eliminators/all/` runs `CMSEliminatorSearch`; `/eliminators/`
        // is the CMS-admin list behind `CMSAHUEliminatorFilter`
        // (cms/filter/frame.py:290, `Meta.fields = []`), which honours spn /
        // mfg_id / series_id and silently IGNORES type, depth, material,
        // vert_sides_gaskets and the nominal width/height. So the count that
        // fires the "narrowed to one row, take it" auto-pick
        // (`checkIfThereIsOnlyOneFoundModel`) was answered by one endpoint and
        // the row it then selected came from another: Material Aluminium +
        // Nominal Depth 2" counted 1 and selected the 1" eliminator, writing
        // `depth_nominal_id: 1` back over the user's 2" — and this form
        // autosaves, so the wrong master was PATCHed.
        //
        // The frame field below keeps the detailed base: `/eliminators/frames/`
        // and `/eliminators/frames/all/` share `CMSFrameFilter`
        // (eliminators_viewset.py:385 / :666), so it was never mis-filtered.
        selectedModelURL: '/api/v1.0/cms/1/v1.0.0/ahu/eliminators/all/',
        componentName: 'eliminator',
        componentsName: 'eliminators',
        paramsFields: [
          'mfg_id',
          'series_id',
          'type_id',
          'depth_nominal_id',
          'mtl',
          'vert_sides_gaskets',
        ],
        filterFields: [
          'mfg_id',
          'series_id',
          'type_id',
          'depth_nominal_id',
          'mtl',
          'vert_sides_gaskets',
          'size_and_qty_id',
        ],
        filtersCount: 2,
        spnCustomSourceField: 'info',
      },
      {
        targetField: 'master_frame_id',
        paramsSourceField: 'frame_params',
        sourceURL: '/api/v1.0/cms/1/v1.0.0/ahu/eliminators/frames/all/',
        selectedModelURL: '/api/v1.0/cms/1/v1.0.0/ahu/eliminators/frames/',
        componentName: 'frame',
        componentsName: 'frames',
        paramsFields: ['mfg_id', 'frame_type_id', 'mtl_id', 'series_id'],
        filterFields: ['mfg_id', 'frame_type_id', 'mtl_id', 'series_id'],
        filtersCount: 2,
        spnCustomSourceField: 'info',
      },
    ],
  }),

  evap_pack: make('evap_pack', {
    subtabs: [
      'media',
      'sump_piping',
      'fill',
      'drain',
      'overflow',
      'accessories',
      'performance',
      'pump',
      'remarks',
    ],
    // evap_pack cms routes are SINGULAR (api/rest_cms/urls.py: ^evap_pack)
    // — verify 6e2a-1 (the plural family 404s and the form never paints).
    cmsParamsURL: '/api/v1.0/cms/1/v1.0.0/ahu/evap_pack/params/',
    withRating: false,
    validatedFields: [
      'master_media_id',
      'qty_wide',
      'delim_depth_id',
      'sump_mtl_gauge_id',
      'pipe_mtl_sch_id',
      'spray_pipe_splash_id',
      'nipple_id',
      'float_valve_id',
      'automatic_valve_id',
      'face_velocity_actual',
      'master_pump_id',
      'fill_id',
      'drain_id',
      'overflow_id',
      'airflow_actual',
    ],
    // All five carry `noFetchCount: true` in legacy (evap_pack/view/form.js
    // :47,:59,:77,:94,:111) — their chrome is `hide` in form.tmpl, so the OLD
    // board never fetches a count for a badge nobody can see.
    catalogueFields: [
      {
        targetField: 'master_media_id',
        paramsSourceField: 'media_params',
        sourceURL: '/api/v1.0/cms/1/v1.0.0/ahu/evap_pack/medias/all/',
        selectedModelURL: '/api/v1.0/cms/1/v1.0.0/ahu/evap_pack/medias/',
        componentName: 'media',
        componentsName: 'medias',
        paramsFields: ['mfg_id', 'model_id', 'edge_finish_id', 'depth_id'],
        filterFields: ['mfg_id', 'model_id', 'edge_finish_id', 'depth_id', 'delim_depth_id'],
        noFetchCount: true,
      },
      {
        targetField: 'master_pump_id',
        paramsSourceField: 'pump_params',
        sourceURL: '/api/v1.0/cms/1/v1.0.0/ahu/evap_pack/pumps/all/',
        selectedModelURL: '/api/v1.0/cms/1/v1.0.0/ahu/evap_pack/pumps/',
        componentName: 'pump',
        componentsName: 'pumps',
        paramsFields: ['mfg_id', 'type_id', 'model', 'power', 'voltage'],
        filterFields: ['mfg_id', 'type_id', 'model'],
        noFetchCount: true,
      },
      {
        targetField: 'fill_id',
        paramsSourceField: 'fill_params',
        sourceURL: '/api/v1.0/cms/1/v1.0.0/ahu/evap_pack/fills/all/',
        selectedModelURL: '/api/v1.0/cms/1/v1.0.0/ahu/evap_pack/fills/',
        componentName: 'fill',
        componentsName: 'fills',
        paramsFields: [
          'material_schedule_id',
          'nipple_nps_id',
          'float_valve_id',
          'automatic_valve_id',
          'nipple_id',
        ],
        filterFields: ['nipple_id', 'float_valve_id', 'fill_conn_hand_id', 'automatic_valve_id'],
        noFetchCount: true,
      },
      {
        targetField: 'drain_id',
        paramsSourceField: 'drain_params',
        sourceURL: '/api/v1.0/cms/1/v1.0.0/ahu/evap_pack/drains/all/',
        selectedModelURL: '/api/v1.0/cms/1/v1.0.0/ahu/evap_pack/drains/',
        componentName: 'drain',
        componentsName: 'drains',
        paramsFields: ['material_schedule_id', 'nipple_nps_id', 'automatic_valve_id', 'nipple_id'],
        filterFields: ['nipple_id', 'drain_conn_hand_id', 'automatic_valve_id'],
        noFetchCount: true,
      },
      {
        targetField: 'overflow_id',
        paramsSourceField: 'overflow_params',
        sourceURL: '/api/v1.0/cms/1/v1.0.0/ahu/evap_pack/oflows/all/',
        selectedModelURL: '/api/v1.0/cms/1/v1.0.0/ahu/evap_pack/oflows/',
        componentName: 'overflow',
        componentsName: 'overflows',
        paramsFields: ['material_schedule_id', 'nipple_nps_id', 'automatic_valve_id', 'nipple_id'],
        filterFields: ['nipple_id', 'automatic_valve_id'],
        noFetchCount: true,
      },
    ],
  }),

  gas_heater: make('gas_heater', {
    subtabs: ['section', 'properties', 'performance', 'bypass', 'pricing', 'remarks'],
    withRating: true,
    withoutRatingSeparateAPI: true,
    validatedFields: [],
    catalogueFields: [
      {
        targetField: 'master_gas_heater_id',
        paramsSourceField: 'gas_heater_params',
        sourceURL: '/api/v1.0/cms/1/v1.0.0/ahu/gas_heaters/all/',
        selectedModelURL: '/api/v1.0/cms/1/v1.0.0/ahu/gas_heaters/',
        componentName: 'gas_heater',
        componentsName: 'gas_heaters',
        paramsFields: [
          'spn',
          'mfg_id',
          'series_id',
          'type_id',
          'configuration',
          'conn_hand_id',
          'heatex_mtl_id',
          'firing_mode_id',
          'fuel_type_id',
          'power_id',
          'certification_id',
          'input_rating_std',
          'output_rating_actual',
          'efficiency',
          'tube_rows_vertically',
        ],
        noFetchCount: true,
      },
    ],
  }),

  silencer: make('silencer', {
    subtabs: ['properties', 'performance'],
    withRating: false, // setupRatingMode callOnDispatcher:false, showCountElement (§50.1)
    validatedFields: [
      'master_silencer_id',
      'qty_wide',
      'qty_high',
      'face_velocity_actual',
      'apd',
      'width',
      'height',
      'length',
      'airflow_actual',
    ],
    alwaysAllowAutoSelect: true, // `allow=true` forced for silencer (§49.3)
    catalogueFields: [
      {
        targetField: 'master_silencer_id',
        paramsSourceField: 'silencer_params',
        sourceURL: '/api/v1.0/cms/1/v1.0.0/ahu/silencers/all/',
        selectedModelURL: '/api/v1.0/cms/1/v1.0.0/ahu/silencers/',
        componentName: 'silencer',
        componentsName: 'silencers',
        paramsFields: [
          'mfg_id',
          'spn',
          'type_id',
          'series_id',
          'shape_id',
          'outside_wall_id',
          'inside_liner_id',
          'media_fill_mtl_id',
          'transmission_loss_id',
        ],
        filterFields: [
          'mfg_id',
          'type_id',
          'series_id',
          'shape_id',
          'width',
          'height',
          'length',
          'outside_wall_id',
          'inside_liner_id',
          'media_fill_mtl_id',
          'transmission_loss_id',
          'airflow_direction_id',
        ],
      },
    ],
  }),

  energy_wheel: make('energy_wheel', {
    subtabs: ['properties', 'performance_summer', 'performance_winter', 'pricing'],
    withRating: true,
    validatedFields: [],
    catalogueFields: [
      {
        targetField: 'master_energy_wheel_id',
        paramsSourceField: 'energy_wheel_params',
        sourceURL: '/api/v1.0/cms/1/v1.0.0/ahu/energy_wheels/all/',
        selectedModelURL: '/api/v1.0/cms/1/v1.0.0/ahu/energy_wheels/',
        componentName: 'energy_wheel',
        componentsName: 'energy_wheels',
        // 38 paramsFields — wheel/rotor/seal/drive/purge/casing/motor/flow.
        paramsFields: [
          'spn', 'mfg_id', 'model', 'fin_pattern_id', 'series_id', 'shape_id',
          'outside_wall_id', 'inside_liner_id', 'media_fill_mtl_id',
          'temp_control_id', 'rotation_detector_id', 'coating_frame_id',
          'coating_media_id', 'frost_control_winter', 'wheel_size_id',
          'purge_angle_id', 'type_id', 'mounting_arr_id', 'fan_locations_id',
          'speed', 'speed_control_id', 'mtr_voltage_id', 'flow_type_id',
          'mtr_fla', 'rotor_depth_id', 'rotor_depth', 'wheel_size', 'seal_id',
          'drive_belt_id', 'fin_thickness_id', 'well_height_id', 'hub_id',
          'wheel_height_id', 'casing_id', 'casing_type_id',
          'purge_sector_location_id', 'configuration_id', 'eatr_oacf',
        ],
        noFetchCount: true,
        counterElementWhenInRatingMode: true,
      },
    ],
  }),

  plate_heat_exchanger: make('plate_heat_exchanger', {
    subtabs: [
      'properties',
      'internal_plates',
      'end_plates',
      'corner_profiles',
      'bypass',
      'recirculation',
      'performance_winter',
      'performance_summer',
      'pricing',
    ],
    withRating: true,
    validatedFields: [],
    catalogueFields: [
      {
        targetField: 'master_phx_id',
        paramsSourceField: 'plate_heat_exchanger_params',
        sourceURL: '/api/v1.0/cms/1/v1.0.0/ahu/plate_heat_exchangers/all/',
        selectedModelURL: '/api/v1.0/cms/1/v1.0.0/ahu/plate_heat_exchangers/',
        componentName: 'plate_heat_exchanger',
        componentsName: 'plate_heat_exchangers',
        // live subset (~25 more commented out in legacy — §50.1).
        paramsFields: [
          'mfg_id',
          'series_id',
          'size_id',
          'flow_type_id',
          'casing_height',
          'casing_height_mm',
          'plate_spacing_code_id',
        ],
        noFetchCount: true,
        counterElementWhenInRatingMode: true,
      },
    ],
  }),

  user_defined_component: make('user_defined_component', {
    subtabs: [
      'section',
      'properties',
      'electrical',
      'pricing',
      'clearances',
      'performance',
      'remarks',
    ],
    withRating: false,
    validatedFields: [],
    catalogueFields: [],
    // Legacy UDC never fetches a per-type cms params doc (no such route —
    // verify 6e2a-1); selects render from master collections + board cms.
    noCmsParamsDoc: true,
  }),
};

/** §50.0 type resolution (factory) — uds/plate/rotary aliases; heat/heatex/
 * wheel partner-tunnel halves skipped (TabComponents already filters). */
export function resolveComponentType(raw: string): ComponentTypeName | null {
  const t = raw.toLowerCase().replace(/[\s-]+/g, '_');
  const aliased =
    t === 'uds' ? 'user_defined_component'
    : t === 'plate' ? 'plate_heat_exchanger'
    : t === 'rotary' ? 'energy_wheel'
    : t;
  return aliased in COMPONENT_FORM_CONFIGS
    ? (aliased as ComponentTypeName)
    : null;
}
